import { Box, Stack, Typography } from "@mui/material";
import { IconChartArrowsVertical, IconUsers } from "@tabler/icons-react";
import { useEffect, useMemo, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { fetchGet, formatStat } from "../util/helpers";
import ViewsLineChart from "../components/ApexLineChart";
import UserContext from "../contexts/UserContext";

export default function CreatorAnalytics() {
    const { campaignId } = useParams();
    const { user } = useContext(UserContext);
    const [analytics, setAnalytics] = useState([]);

    useEffect(() => {
        if (campaignId && user.userDocId) {
            fetchAnalytics();
        }
    }, [campaignId, user.userDocId]);

    const fetchAnalytics = async () => {
        try {
            const result = await fetchGet(`/brand/campaigns/${campaignId}/creator/${user.userDocId}/analytics`);
            if (result.result) {
                result.result.sort((a, b) => new Date(a.date) - new Date(b.date));
                setAnalytics(result.result);
            }
        } catch (error) {
            console.log("error fetching analytics", error);
        }
    };

    const allData = useMemo(() => {
        const total = {};
        const result = [];
        analytics.forEach((data) => {
            const formattedDate = new Date(data.date).toISOString().slice(0, 10);
            if (total[formattedDate] === undefined) {
                total[formattedDate] = {
                    views: 0,
                    shares: 0,
                    likes: 0,
                    comments: 0,
                    engagement: 0,
                };
            }
            total[formattedDate].views += data.views || 0;
            total[formattedDate].shares += data.shares || 0;
            total[formattedDate].likes += data.likes || 0;
            total[formattedDate].comments += data.comments || 0;
            total[formattedDate].engagement += data.engagement || 0;
        });
        for (let key in total) {
            result.push([total[key], key]);
        }
        result.sort((a, b) => new Date(a[1]) - new Date(b[1]));
        return [
            result.map((r) => r[0]),
            result.map((r) => new Date(r[1]).toISOString().slice(0, 10)),
        ];
    }, [analytics]);

    const allYValues = allData[0];

    // Calculate totals
    const totalViews = useMemo(() => {
        return allYValues.reduce((sum, data) => sum + (data.views || 0), 0);
    }, [allYValues]);

    const totalEngagement = useMemo(() => {
        return allYValues.reduce((sum, data) => sum + (data.engagement || 0), 0);
    }, [allYValues]);

    const engagementRate = useMemo(() => {
        if (totalViews === 0) return 0;
        return ((totalEngagement / totalViews) * 100).toFixed(2);
    }, [totalViews, totalEngagement]);

    if (analytics.length === 0) {
        return (
            <Stack direction="column" spacing={2} sx={{ padding: "3%", alignItems: "center", justifyContent: "center", minHeight: "400px" }}>
                <Typography variant="h4" color="var(--main-text-color)">No Analytics Data Yet</Typography>
                <Typography variant="bodysm" color="var(--body-text-color-600)">
                    Analytics will appear here once you start posting content for this campaign.
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack direction="column" spacing={2} sx={{ padding: "3%" }}>
            <Typography variant="h3" color={"var(--main-text-color)"}>
                My Performance
            </Typography>
            <Stack direction="row" spacing={1}>
                <Box className="box">
                    <Stack
                        direction="column"
                        spacing={3}
                        height="100%"
                        justifyContent="space-between"
                    >
                        <Typography
                            variant="bodyregm"
                            color="var(--body-text-color-600)"
                        >
                            Total Views
                        </Typography>
                        <Typography variant="h2">
                            {formatStat(totalViews)}
                        </Typography>
                    </Stack>
                </Box>
                <Box className="box">
                    <Stack
                        direction="column"
                        spacing={3}
                        height="100%"
                        justifyContent="space-between"
                    >
                        <Typography
                            variant="bodyregm"
                            color="var(--body-text-color-600)"
                        >
                            Total Engagement
                        </Typography>
                        <Typography variant="h2">
                            {formatStat(totalEngagement)}
                        </Typography>
                    </Stack>
                </Box>
                <Box className="box">
                    <Stack
                        direction="column"
                        spacing={3}
                        height="100%"
                        justifyContent="space-between"
                    >
                        <Typography
                            variant="bodyregm"
                            color="var(--body-text-color-600)"
                        >
                            Engagement Rate
                        </Typography>
                        <Typography variant="h2">
                            {engagementRate}%
                        </Typography>
                    </Stack>
                </Box>
            </Stack>
            <Stack direction="column" spacing={2} className="box">
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                            className="gradient-circle"
                            sx={{
                                padding: "8px",
                                alignItems: "center",
                                justifyItems: "center",
                            }}
                        >
                            <IconChartArrowsVertical size="20" className="icon" />
                        </Box>
                        <Typography variant="h4" color={"var(--main-text-color)"}>
                            Views Over Time
                        </Typography>
                    </Stack>
                </Stack>
                <ViewsLineChart data={allData} dataKey="views" />
            </Stack>
            <Stack direction="column" spacing={2} className="box">
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                            className="gradient-circle"
                            sx={{
                                padding: "8px",
                                alignItems: "center",
                                justifyItems: "center",
                            }}
                        >
                            <IconChartArrowsVertical size="20" className="icon" />
                        </Box>
                        <Typography variant="h4" color={"var(--main-text-color)"}>
                            Engagement Over Time
                        </Typography>
                    </Stack>
                </Stack>
                <ViewsLineChart data={allData} dataKey="engagement" />
            </Stack>
        </Stack>
    );
}

